import 'styled-components';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Col, Row, Text } from '@keyslabor/shared/ui';
import { Logo } from '@/assets';
import { FlagSpain, FlagUsa } from '@/assets/index';
import { useNavigate } from '@/hooks/useNavigate.ts';
import LayoutPageMobile from '@/components/LayoutPageMobile';
import { useQuestionsAnswers } from '@/hooks/useQuestionsAnswers';
import { Language, StatusOutput } from '@/api/types';
import { routes } from '@/constants.ts';

const useAvailableLanguages = (data: StatusOutput | undefined) => {
  return useMemo(() => {
    const languages: Language[] = [];
    if (!data)
      return [];
    if (data?.english_intro) {
      languages.push('english');
    }
    if (data?.spanish_intro) {
      languages.push('spanish');
    }
    if (data?.russian_intro) {
      languages.push('russian');
    }
    return languages;
  }, [data]);
}

function Welcome() {
  const { setLanguage, remoteStatus: data } = useQuestionsAnswers();

  const isEngAvailable = data?.english_intro;
  const isSpanishAvailable = data?.spanish_intro;
  const isRussianAvailable = data?.russian_intro;
  const name = data?.name;
  const availableLanguages = useAvailableLanguages(data);
  const [params] = useSearchParams();
  const langFromParams = params.get('lang') as Language | null;

  const navigate = useNavigate();

  const handleNext = (language: Language) => {
    setLanguage(language);
    navigate(routes.tutorial);
  };

  useEffect(() => {
    if (data && availableLanguages.length === 1) {
      handleNext(availableLanguages[0]);
    }

    if (langFromParams && availableLanguages.includes(langFromParams)) {
      handleNext(langFromParams);
    }
  }, [data, availableLanguages, langFromParams]);

  return (
    <LayoutPageMobile pb="20px">
      <Row flexCenter>
        <Logo />
        <Text fontWeight="900" mt="30px" fontSize="18px" upper>
          Hello
        </Text>
        <Text fontWeight="900" mt="30px" fontSize="24px" upper>
          {name}!
        </Text>
        <Row flexCenter mt="60px">
          <Text fontWeight="900" fontSize="20px" upper>
            Welcome to
          </Text>
        </Row>
        <Row flexCenter mt="10px">
          <Text fontWeight="800" fontSize="23px" upper>
            {data?.client_name}
          </Text>
        </Row>
        <Text
          fontWeight="800"
          fontSize="18px"
          maxWidth="220px"
          upper
          center
          mt="40px"
        >
          Please select interview language
        </Text>

        <Col gap="60px" mt="40px">
          {isEngAvailable && (
            <Row
              gap="20px"
              flexCenter
              css="cursor: pointer;"
              onClick={() => handleNext('english')}
            >
              <FlagUsa />
              <Text fontWeight="800" fontSize="18px" upper>
                English
              </Text>
            </Row>
          )}
          {isSpanishAvailable && (
            <Row
              gap="20px"
              flexCenter
              onClick={() => handleNext('spanish')}
              css="cursor: pointer;"
            >
              <FlagSpain />
              <Text fontWeight="800" fontSize="18px" upper>
                Español
              </Text>
            </Row>
          )}
        </Col>
        {isRussianAvailable && (
          <Col gap="60px" mt="40px">
              <Row
                gap="20px"
                flexCenter
                css="cursor: pointer;"
                onClick={() => handleNext('russian')}
              >
                <Text fontWeight="800" fontSize="14px" upper>
                  Русский
                </Text>
              </Row>
          </Col>
        )}
      </Row>
    </LayoutPageMobile>
  );
}

export default Welcome;
